/**
 * RIST Restart API
 *
 * This module handles API calls for restarting on-prem RIST MediaConnect channels
 * using AWS Step Functions.
 */

import ApiConfig from "../services/ApiConfig";
import axios from "axios";
import environmentMapping from "../config/environmentMapping.json";

/**
 * Restart on-prem RIST channels by invoking the appropriate Step Function
 *
 * @param {string[]} channels - MediaConnect channel names, e.g. ["CH33_DCA_Sec"]
 * @param {string} environment - The environment (short code or full name)
 * @param {string} eventId - The event the restart was triggered from, recorded in the action log
 * @returns {Promise<Object>} Response object with success/error information
 */
const ristRestartApi = async (channels, environment, eventId) => {
    try {
        // Validate inputs
        if (!channels || !Array.isArray(channels) || channels.length === 0 || !environment) {
            throw new Error("Invalid parameters: channels and environment are required");
        }

        // Map short environment codes to full names
        const fullEnvironmentName = environmentMapping[environment] || environment;

        // Get API configuration
        var config = await ApiConfig();
        config.method = "post";
        config.url += "/restart_onprem_rist";

        // Prepare request payload
        config.data = {
            environment: fullEnvironmentName,
            mediaConnectChannel: channels,
            // Carried for the action log only; the Step Function is channel scoped.
            eventId: eventId
        };

        // Make the API call
        return await axios(config)
            .then(function (response) {
                return response.data;
            })
            .catch(function (error) {
                // Handle different types of errors
                if (error.response) {
                    // Server responded with error status
                    const status = error.response.status;
                    const data = error.response.data;

                    // Handle specific error cases
                    if (status === 409 && data && data.alreadyExists) {
                        return {
                            message: "RIST restart already in progress for these channels",
                            alreadyExists: true,
                            executionArn: data.executionArn || null
                        };
                    } else if (status === 403) {
                        throw new Error("Access denied to Step Function. Please check IAM permissions or contact the administrator.");
                    } else if (status === 500) {
                        // 500 can be infrastructure errors (like missing cross-account role) or server errors
                        const message = data?.message || data?.error || "An internal server error occurred. Please try again or contact support.";
                        throw new Error(message);
                    } else if (status === 400) {
                        // Generic bad request message
                        const message = data?.message || data?.error || "Invalid request. Please check your input and try again.";
                        throw new Error(message);
                    } else if (data && data.message) {
                        throw new Error(data.message);
                    } else {
                        throw new Error(`Server error (${status}). Please try again or contact support.`);
                    }
                } else if (error.request) {
                    // Request was made but no response received (network error)
                    throw new Error("Network error. Please check your connection and try again.");
                } else {
                    // Something else happened (error in request setup)
                    throw new Error(error.message || "An unexpected error occurred. Please try again.");
                }
            });
    } catch (error) {
        console.error("Error in ristRestartApi:", error);
        throw error;
    }
};

export default ristRestartApi;
